import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  ArrowRight, 
  AlertTriangle, 
  Trophy, 
  TrendingUp, 
  FileText, 
  X, 
  Download,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

// Risk Badge - Idéntico a tu diseño original
const getRiskFromScore = (contract) => {
  const score = parseFloat(
    contract.key_metrics?.calificacion || contract.risk_score
  );

  if (!isNaN(score)) {
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'low';
  }

  return 'low';
};

const RiskBadge = ({ level, score }) => {
  const config = {
    low: { bg: '#D1FAE5', text: '#065F46', label: 'Bajo' },
    medium: { bg: '#FEF3C7', text: '#92400E', label: 'Medio' },
    high: { bg: '#FEE2E2', text: '#991B1B', label: 'Alto' }
  };
  const style = config[level] || config.medium;
  
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
      style={{ backgroundColor: style.bg, color: style.text }}>
      {style.label} {score && `(${score})`}
    </span>
  );
};

const ComparisonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const data = await api.getComparisons();
        const found = data.find(c => c.id === id);
        
        if (found) {
          // Adaptador: El historial guarda la info de la IA en "comparison_result"
          // y los contratos en "contracts_data". Lo mapeamos para que sea idéntico
          // a la respuesta en vivo.
          const aiResult = found.comparison_result || {};
          const mappedComparison = {
            comparison_id: found.id,
            contracts: found.contracts_data || found.contracts || [],
            comparison_summary: aiResult.resumen_comparativo || found.comparison_summary,
            best_option: aiResult.mejor_opcion || found.best_option,
            advantages: aiResult.ventajas_por_contrato || found.advantages,
            disadvantages: aiResult.desventajas_por_contrato || found.disadvantages,
            alerts: aiResult.alertas || found.alerts,
            final_recommendation: aiResult.recomendacion_final || found.final_recommendation,
            recommendation_by_profile: aiResult.recomendacion_segun_perfil || found.recommendation_by_profile,
            created_at: found.created_at
          };
          
          setComparison(mappedComparison);
        }
      } catch (error) {
        toast.error("Error al cargar el detalle");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#1A4D2E' }}></div>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="min-h-screen p-10 text-center text-gray-500" style={{ backgroundColor: '#F5F5F0' }}>
        No se encontró la comparación
        <br />
        <Button onClick={() => navigate('/dashboard')} className="mt-4" style={{ backgroundColor: '#1A4D2E' }}>
          Volver al Inicio
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12" style={{ backgroundColor: '#F5F5F0' }}>
      {/* Navbar simplificada para el detalle */}
      <nav className="bg-white border-b border-border shadow-sm sticky top-0 z-10 mb-6">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mr-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
              <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>ContractCheck360</span>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">Dashboard</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
                Historial de Comparación
              </h1>
              <p className="text-sm text-gray-500">Fecha: {new Date(comparison.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            
            <Button
              size="sm"
              style={{ backgroundColor: '#1A4D2E' }}
              onClick={async () => {
                try {
                  const blobData = await api.downloadComparisonPdf(comparison.comparison_id);
                  const blob = new Blob([blobData], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Comparacion_${comparison.comparison_id.substring(0,8)}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  toast.success('PDF descargado');
                } catch (err) {
                  let errorMsg = 'Error al descargar PDF';
                  if (err.response?.data instanceof Blob) {
                    try {
                      const text = await err.response.data.text();
                      const parsed = JSON.parse(text);
                      errorMsg = parsed.detail || errorMsg;
                    } catch { /* ignore parse errors */ }
                  }
                  toast.error(errorMsg);
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" /> Descargar PDF
            </Button>
          </div>

          {/* Winner Card */}
          {comparison.best_option && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-green-100">
                  <Trophy className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-green-800">Mejor Opción</h2>
              </div>
              <p className="text-lg font-semibold text-green-700 mb-2">
                Contrato #{comparison.best_option.numero}: {comparison.best_option.nombre || 'Recomendado'}
              </p>
              <p className="text-sm text-green-600">{comparison.best_option.justificacion}</p>
            </div>
          )}

          {/* Summary */}
          {comparison.comparison_summary && (
            <div className="bg-white border shadow-sm rounded-lg p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" style={{ color: '#1A4D2E' }} />
                Resumen Comparativo
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">{comparison.comparison_summary}</p>
            </div>
          )}

          {/* Comparison Table */}
          <div className="bg-white border shadow-sm rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold">Comparación Detallada</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ backgroundColor: '#1A4D2E' }}>
                    <th className="text-left p-3 text-white font-medium">Métrica</th>
                    {comparison.contracts.map((c, idx) => (
                      <th key={idx} className="text-left p-3 text-white font-medium">
                        Contrato {idx + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-medium text-sm">Archivo</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm">{c.file_name}</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-medium text-sm">Riesgo</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3">
                        <RiskBadge level={getRiskFromScore(c)} score={c.key_metrics?.calificacion || c.risk_score} />
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium text-sm">Tasa Interés</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm">{c.key_metrics?.tasa_interes || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-medium text-sm">CAT</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm">{c.key_metrics?.cat || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium text-sm">Monto Total</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm">{c.key_metrics?.monto_total || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-medium text-sm">Plazo</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm">{c.key_metrics?.plazo || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium text-sm">Calificación</td>
                    {comparison.contracts.map((c, idx) => (
                      <td key={idx} className="p-3 text-sm font-bold" style={{ color: '#1A4D2E' }}>
                        {c.key_metrics?.calificacion ? `${c.key_metrics.calificacion}/10` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Pros & Cons */}
          {(comparison.advantages || comparison.disadvantages) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {comparison.contracts.map((c, idx) => {
                const key = `contrato_${idx + 1}`;
                const advs = (comparison.advantages || {})[key] || [];
                const disadvs = (comparison.disadvantages || {})[key] || [];
                if (advs.length === 0 && disadvs.length === 0) return null;
                
                return (
                  <div key={key} className="bg-white border shadow-sm rounded-lg p-4">
                    <h4 className="font-semibold text-green-700 mb-2">
                      Ventajas Contrato {idx + 1}
                    </h4>
                    <ul className="space-y-1">
                      {advs.map((v, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {v}
                        </li>
                      ))}
                    </ul>
                    {disadvs.length > 0 && (
                      <>
                        <h4 className="font-semibold text-red-700 mt-4 mb-2">
                          Desventajas
                        </h4>
                        <ul className="space-y-1">
                          {disadvs.map((d, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                              {d}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Alerts */}
          {comparison.alerts && comparison.alerts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Puntos Importantes
              </h4>
              <ul className="space-y-1">
                {comparison.alerts.map((alert, i) => (
                  <li key={i} className="text-sm text-amber-700">• {alert}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Final Recommendation */}
          {comparison.final_recommendation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Recomendación Final</h4>
              <p className="text-sm text-blue-700">{comparison.final_recommendation}</p>
            </div>
          )}

          {/* Recommendation by Profile */}
          {comparison.recommendation_by_profile && Object.keys(comparison.recommendation_by_profile).length > 0 && (
            <div className="bg-white border shadow-sm rounded-lg p-4">
              <h4 className="font-semibold mb-3">Según tu Perfil</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(comparison.recommendation_by_profile).map(([profile, rec]) => (
                  <div key={profile} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      {profile.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-gray-700">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonDetail;
